/**
 * EAA-2026: Question-type group service — tenant-owned grouping of AI question types
 * for the AI generation dialogs, and the CRUD behind the "Nhóm dạng câu hỏi" screen.
 */

import { sharedApiClient } from './client';
import type {
  CreateQuestionTypeGroupData,
  QuestionTypeGroup,
  QuestionTypeGroupCatalog,
  QuestionTypeGroupListParams,
  QuestionTypeGroupListResponse,
  UpdateQuestionTypeGroupData,
} from './questionTypeGroup.types';

class QuestionTypeGroupService {
  private basePath = '/content/question-type-groups';

  /** Append ``?tenant_id=`` for staff acting on a tenant (tenant users resolve it from the header). */
  private withTenant(path: string, tenantId?: string): string {
    if (!tenantId) return path;
    const sep = path.includes('?') ? '&' : '?';
    return `${path}${sep}tenant_id=${encodeURIComponent(tenantId)}`;
  }

  /** GET content/question-type-groups/catalog/ — groups + ungrouped types for the dialogs. */
  async getCatalog(tenantId?: string): Promise<QuestionTypeGroupCatalog> {
    const response = await sharedApiClient.get<QuestionTypeGroupCatalog>(
      this.withTenant(`${this.basePath}/catalog/`, tenantId)
    );
    return response.data;
  }

  /** GET content/question-type-groups/ — paginated list. */
  async listGroups(params?: QuestionTypeGroupListParams): Promise<QuestionTypeGroupListResponse> {
    const query = new URLSearchParams();
    if (params?.tenant_id) query.append('tenant_id', params.tenant_id);
    if (params?.page) query.append('page', String(params.page));
    if (params?.page_size) query.append('page_size', String(params.page_size));
    if (params?.search) query.append('search', params.search);
    if (params?.is_active !== undefined) query.append('is_active', String(params.is_active));
    if (params?.ordering) query.append('ordering', params.ordering);
    const qs = query.toString();
    const response = await sharedApiClient.get<QuestionTypeGroupListResponse>(
      qs ? `${this.basePath}/?${qs}` : `${this.basePath}/`
    );
    return response.data;
  }

  async getGroup(id: string, tenantId?: string): Promise<QuestionTypeGroup> {
    const response = await sharedApiClient.get<QuestionTypeGroup>(
      this.withTenant(`${this.basePath}/${id}`, tenantId)
    );
    return response.data;
  }

  async createGroup(data: CreateQuestionTypeGroupData, tenantId?: string): Promise<QuestionTypeGroup> {
    const response = await sharedApiClient.post<QuestionTypeGroup>(
      this.withTenant(`${this.basePath}/`, tenantId),
      data
    );
    return response.data;
  }

  /** Partial update; ``ai_content_type_ids`` (when sent) replaces the members in order. */
  async updateGroup(
    id: string,
    data: UpdateQuestionTypeGroupData,
    tenantId?: string
  ): Promise<QuestionTypeGroup> {
    const response = await sharedApiClient.put<QuestionTypeGroup>(
      this.withTenant(`${this.basePath}/${id}`, tenantId),
      data
    );
    return response.data;
  }

  async deleteGroup(id: string, tenantId?: string): Promise<void> {
    await sharedApiClient.delete(this.withTenant(`${this.basePath}/${id}`, tenantId));
  }

  /** PUT …/{id}/types/ — replace the members with this ordered list. */
  async setGroupTypes(id: string, aiContentTypeIds: string[], tenantId?: string): Promise<QuestionTypeGroup> {
    const response = await sharedApiClient.put<QuestionTypeGroup>(
      this.withTenant(`${this.basePath}/${id}/types/`, tenantId),
      { ai_content_type_ids: aiContentTypeIds }
    );
    return response.data;
  }

  /** POST …/reorder/ — every group id of the tenant, in the new display order. */
  async reorderGroups(ids: string[], tenantId?: string): Promise<void> {
    await sharedApiClient.post(this.withTenant(`${this.basePath}/reorder/`, tenantId), { ids });
  }
}

export const questionTypeGroupService = new QuestionTypeGroupService();

export default questionTypeGroupService;
